import { useState, useEffect } from "react";
import Cookies from "js-cookie";
import type { UIMessage } from "ai";
import { Edit2, Copy, Trash2, Brain, Share2, Download } from "lucide-react";
import { deleteChat } from "instantdb/client/chat";
import { MarkdownRenderer } from "@/components/ui/markdown-renderer";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useNotification } from "@/hooks/useNotification";
import { ModelSelectionDropdown } from "@/components/chat/ModelSelectionDropdown";
import { imageModels } from "@/lib/aiproviders/models";
import { COOKIE_NAMES, COOKIE_EXPIRY_DAYS } from "@/lib/constants/cookies";
import { deleteMessage, upsertMessage } from "instantdb/client/message";
import { AIEditMessage } from "./AIEditMessage";
import { useParams, useRouter } from "@tanstack/react-router";
import { useSetAtom } from "jotai";
import { shareDialogAtom } from "@/state/dialogs";
import { useAIUsageCheck } from "@/hooks/useAIUsageCheck";

interface AIMessageProps {
  message: UIMessage & { metadata: { model: string } };
  messages: UIMessage[];
  currentModel?: string;
  onRetry?: (content: string, model: string) => void;
  onModelChange?: (model: string) => void;
  isOwner: boolean;
  imageMode?: boolean;
  onImageClick?: (imageUrl: string) => void;
}

export function AIMessage({
  message,
  messages,
  currentModel,
  onRetry,
  onModelChange,
  isOwner,
  imageMode = false,
  onImageClick,
}: AIMessageProps) {
  const isUser = message.role === "user";
  const { showNotification } = useNotification();
  const [showReasoning, setShowReasoning] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const params = useParams({ strict: false });
  const router = useRouter();
  const { checkUsage } = useAIUsageCheck();
  const setShareDialogOpen = useSetAtom(shareDialogAtom);

  useEffect(() => {
    // Check if device supports touch
    const checkTouch = () => {
      setIsTouchDevice(
        "ontouchstart" in window ||
          navigator.maxTouchPoints > 0 ||
          window.matchMedia("(pointer: coarse)").matches,
      );
    };

    checkTouch();

    // Listen for changes in input capabilities
    const mediaQuery = window.matchMedia("(pointer: coarse)");
    const handleChange = () => checkTouch();

    // Add event listener for media query changes
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  // Get the model used for this specific message from metadata
  const messageModel = message.metadata?.model || currentModel;

  // Check if this message uses an image model
  const isImageMessage = imageModels.some((model) => model.id === messageModel);

  // Extract reasoning from message parts if it exists
  const reasoningPart = message.parts?.find((part: any) => part.type === "reasoning");
  const hasReasoning = !isUser && reasoningPart?.content;

  const handleCopy = async () => {
    try {
      // Check if message has image parts
      const imageParts = message.parts?.filter((part) => part.type === "image") || [];
      const textParts = message.parts?.filter((part) => part.type === "text") || [];

      if (imageParts.length > 0) {
        // For image messages
        const originalImageUrl = imageParts[0].url;

        try {
          // Use our proxy endpoint to avoid CORS issues
          const proxiedImageUrl = `/api/image-proxy?url=${encodeURIComponent(originalImageUrl)}`;

          // Fetch the image through our proxy
          const response = await fetch(proxiedImageUrl);
          const blob = await response.blob();

          // Try to copy to clipboard
          if (navigator.clipboard && "write" in navigator.clipboard) {
            const item = new ClipboardItem({
              [blob.type]: blob,
            });
            await navigator.clipboard.write([item]);
            showNotification({
              type: "success",
              message: "Image copied to clipboard",
            });
          } else {
            // Fallback: Copy image URL
            await navigator.clipboard.writeText(originalImageUrl);
            showNotification({
              type: "success",
              message: "Image URL copied to clipboard",
            });
          }
        } catch (error) {
          // If image copying fails, copy the URL
          console.log("Image copy failed, copying URL instead:", error);
          try {
            await navigator.clipboard.writeText(originalImageUrl);
            showNotification({
              type: "info",
              message: "Image URL copied (direct image copy not available)",
            });
          } catch (urlError) {
            console.error("Failed to copy URL:", urlError);
            showNotification({
              type: "error",
              message: "Failed to copy",
            });
          }
        }
      } else {
        // For text messages, copy text content
        const textContent = textParts.map((part) => part.text).join("\n") || "";

        if (!textContent.trim()) {
          showNotification({
            type: "error",
            message: "No content to copy",
          });
          return;
        }

        await navigator.clipboard.writeText(textContent);
        showNotification({
          type: "success",
          message: "Text copied to clipboard",
        });
      }
    } catch (error) {
      console.error("Copy failed:", error);
      showNotification({
        type: "error",
        message: "Failed to copy",
      });
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleRetry = async (selectedModel: string) => {
    // Check if user has usage rights for this model
    if (!checkUsage(selectedModel)) {
      return;
    }

    try {
      const currentIndex = messages.findIndex((m) => m.id === message.id);

      if (currentIndex === -1) {
        showNotification({
          type: "error",
          message: "Message not found",
        });
        return;
      }

      let userMessage: UIMessage;
      let userMessageId: string;
      let aiResponseId: string | null = null;

      if (isUser) {
        // Retrying from user message
        userMessage = message;
        userMessageId = message.id;
        // Check for AI response to delete
        if (currentIndex + 1 < messages.length && messages[currentIndex + 1].role === "assistant") {
          aiResponseId = messages[currentIndex + 1].id;
        }
      } else {
        // Retrying from AI message - get the previous user message
        if (currentIndex > 0 && messages[currentIndex - 1].role === "user") {
          userMessage = messages[currentIndex - 1];
          userMessageId = userMessage.id;
          aiResponseId = message.id;
        } else {
          showNotification({
            type: "error",
            message: "No user message found to retry",
          });
          return;
        }
      }

      // Extract content from user message
      // For image generation, user messages will always be text prompts
      const content =
        userMessage.parts
          ?.filter((part: any) => part.type === "text")
          .map((part: any) => part.text)
          .join("\n") || "";

      // Validate that we have content to retry with
      if (!content.trim()) {
        showNotification({
          type: "error",
          message: "No content found to retry",
        });
        return;
      }

      // Update the model selection cookie and local state
      // Determine if the selected model is an image model
      const isImageModel = imageModels.some((m) => m.id === selectedModel);
      if (isImageModel) {
        Cookies.set(COOKIE_NAMES.SELECTED_IMAGE_MODEL, selectedModel, {
          expires: COOKIE_EXPIRY_DAYS,
        });
      } else {
        Cookies.set(COOKIE_NAMES.SELECTED_TEXT_MODEL, selectedModel, {
          expires: COOKIE_EXPIRY_DAYS,
        });
      }
      onModelChange?.(selectedModel);

      // Update user message model if different
      const currentUserModel = userMessage.metadata?.model;
      if (currentUserModel !== selectedModel && params.chatId) {
        // Keep all existing fields but update the model
        await upsertMessage(
          {
            id: userMessageId,
            model: selectedModel,
            updatedAt: new Date().toISOString(),
          },
          params.chatId,
        );
      }

      // Delete the AI response
      if (aiResponseId) {
        await deleteMessage(aiResponseId);
      }

      // Trigger AI regeneration
      onRetry?.(content, selectedModel);
    } catch (error) {
      console.error("Error retrying message:", error);
      showNotification({
        type: "error",
        message: "Failed to retry message",
      });
    }
  };

  const handleDelete = async () => {
    try {
      // Find the current message index
      const currentIndex = messages.findIndex((m) => m.id === message.id);

      if (currentIndex === -1) {
        showNotification({
          type: "error",
          message: "Message not found",
        });
        return;
      }

      // Determine which messages to delete based on role
      const messagesToDelete: string[] = [];

      if (isUser) {
        // For user messages, validate that next message is an assistant message
        messagesToDelete.push(message.id);
        if (currentIndex + 1 < messages.length && messages[currentIndex + 1].role === "assistant") {
          messagesToDelete.push(messages[currentIndex + 1].id);
        }
      } else {
        // For AI messages, validate that previous message is a user message
        messagesToDelete.push(message.id);
        if (currentIndex > 0 && messages[currentIndex - 1].role === "user") {
          messagesToDelete.push(messages[currentIndex - 1].id);
        }
      }

      // Check if we're deleting all messages in the chat
      const messageIds = messages.map((m) => m.id);
      const remainingMessages = messageIds.filter((id) => !messagesToDelete.includes(id));

      if (remainingMessages.length === 0 && params.chatId) {
        // Delete the entire chat if no messages remain
        await deleteChat(params.chatId);

        // Navigate back to home
        router.navigate({ to: "/" });

        showNotification({
          type: "success",
          message: "Chat deleted",
        });
      } else {
        // Delete the messages
        await Promise.all(messagesToDelete.map((id) => deleteMessage(id)));

        showNotification({
          type: "success",
          message: "Message deleted",
        });
      }
    } catch (error) {
      console.error("Error deleting message:", error);
      showNotification({
        type: "error",
        message: "Failed to delete message",
      });
    }
  };

  const handleShare = () => {
    if (params.chatId) {
      setShareDialogOpen(true);
    }
  };

  const handleImageDownload = async (imageUrl: string) => {
    try {
      // Use our proxy endpoint to avoid CORS issues
      const proxiedImageUrl = `/api/image-proxy?url=${encodeURIComponent(imageUrl)}`;

      // Fetch the image through our proxy
      const response = await fetch(proxiedImageUrl);
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Extract filename from URL or use default
      const urlParts = imageUrl.split("/");
      const filename = urlParts[urlParts.length - 1] || "generated-image.png";
      link.download = filename;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      showNotification({
        type: "error",
        message: "Failed to download image",
      });
    }
  };

  return (
    <div className={`group flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className="flex flex-col gap-2">
        <div className={`rounded-lg w-fit ml-auto ${isUser ? "p-4 bg-muted" : "px-4 w-full"}`}>
          {isUser && isEditing ? (
            <AIEditMessage
              message={message}
              messages={messages}
              currentModel={currentModel}
              onRetry={onRetry}
              onCancel={handleCancelEdit}
            />
          ) : (
            message.parts?.map((part, index) => {
              switch (part.type) {
                case "text":
                  return isUser ? (
                    <p key={index} className="whitespace-pre-wrap select-text">
                      {part.text}
                    </p>
                  ) : (
                    <div key={index} className="select-text">
                      <MarkdownRenderer content={part.text} />
                    </div>
                  );
                case "image":
                  return (
                    <div key={index} className="mt-2 relative group/image">
                      <img
                        src={part.url}
                        alt="Generated image"
                        className="max-w-full h-auto rounded-lg shadow-md cursor-pointer hover:opacity-90 transition-opacity"
                        style={{ maxHeight: "400px" }}
                        onClick={() => onImageClick?.(part.url)}
                        onError={(e) => {
                          console.error("Image failed to load:", part.url);
                        }}
                      />
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="secondary"
                              size="icon"
                              className={`absolute top-2 right-2 h-10 w-10 md:h-8 md:w-8 transition-opacity ${
                                isTouchDevice
                                  ? "opacity-100"
                                  : "opacity-0 group-hover/image:opacity-100"
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleImageDownload(part.url);
                              }}
                            >
                              <Download className="h-5 w-5 md:h-4 md:w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom">
                            <p>Download</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  );
                case "reasoning":
                  // Skip rendering reasoning in main content area
                  return null;
                default:
                  return null;
              }
            })
          )}

          {/* Show reasoning section if available and toggled on */}
          {hasReasoning && showReasoning && (
            <div className="mt-3 pt-3 border-t border-muted-foreground/20">
              <div className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                <Brain className="h-4 w-4 md:h-3 md:w-3" />
                Model Reasoning
              </div>
              <div className="text-sm text-muted-foreground/80 italic select-text">
                <MarkdownRenderer content={reasoningPart.content} />
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        {!isEditing && (
          <div
            className={`flex gap-2 md:gap-1 ${isUser ? "justify-end mb-4" : "px-2 justify-start"}`}
          >
            <TooltipProvider>
              {isOwner && (
                <ModelSelectionDropdown
                  currentModel={messageModel}
                  onSelect={handleRetry}
                  onOpenChange={setDropdownOpen}
                  isImageMode={imageMode}
                  className={`h-10 w-10 md:h-7 md:w-7 transition-opacity ${
                    dropdownOpen || isTouchDevice
                      ? "opacity-100"
                      : "opacity-0 group-hover:opacity-100"
                  }`}
                  tooltipContent="Retry"
                  side="bottom"
                  align="start"
                />
              )}

              {/* Show reasoning toggle for assistant messages with reasoning */}
              {hasReasoning && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`h-10 w-10 md:h-7 md:w-7 transition-opacity ${
                        showReasoning
                          ? "opacity-100 text-primary"
                          : dropdownOpen || isTouchDevice
                            ? "opacity-100"
                            : "opacity-0 group-hover:opacity-100"
                      }`}
                      onClick={() => setShowReasoning(!showReasoning)}
                    >
                      <Brain className="h-4 w-4 md:h-3 md:w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>{showReasoning ? "Hide" : "Show"} Reasoning</p>
                  </TooltipContent>
                </Tooltip>
              )}

              {isUser && isOwner && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`h-10 w-10 md:h-7 md:w-7 transition-opacity ${
                        dropdownOpen || isTouchDevice
                          ? "opacity-100"
                          : "opacity-0 group-hover:opacity-100"
                      }`}
                      onClick={handleEdit}
                    >
                      <Edit2 className="h-4 w-4 md:h-3 md:w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>Edit</p>
                  </TooltipContent>
                </Tooltip>
              )}

              {isOwner && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`h-10 w-10 md:h-7 md:w-7 transition-opacity ${
                        dropdownOpen || isTouchDevice
                          ? "opacity-100"
                          : "opacity-0 group-hover:opacity-100"
                      }`}
                      onClick={handleShare}
                    >
                      <Share2 className="h-4 w-4 md:h-3 md:w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>Share</p>
                  </TooltipContent>
                </Tooltip>
              )}

              {isOwner && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`h-10 w-10 md:h-7 md:w-7 transition-opacity ${
                        dropdownOpen || isTouchDevice
                          ? "opacity-100"
                          : "opacity-0 group-hover:opacity-100"
                      }`}
                      onClick={handleDelete}
                    >
                      <Trash2 className="h-4 w-4 md:h-3 md:w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>Delete</p>
                  </TooltipContent>
                </Tooltip>
              )}

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`h-10 w-10 md:h-7 md:w-7 transition-opacity ${
                      dropdownOpen || isTouchDevice
                        ? "opacity-100"
                        : "opacity-0 group-hover:opacity-100"
                    }`}
                    onClick={handleCopy}
                  >
                    <Copy className="h-4 w-4 md:h-3 md:w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Copy</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
      </div>
    </div>
  );
}
